import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { MultiPageVideoModel } from '@bynder-studio/render-core';
import {
    Button,
    Checkbox,
    CheckboxGroup,
    Dropdown,
    Flex,
    Form,
    InlineBanner,
    ModalBase,
    Tooltip,
} from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useExport from 'packages/variation-export/hooks/useExport';
import useDesign from 'packages/pages/design/hooks/useDesign';
import { catchResponseError } from 'packages/helpers/helpers';
import useAccessRights from 'packages/hooks/useAccessRights';
import { sendAppcuesEvent } from 'packages/helpers/RouteWithAppcues';
import features from 'packages/configs/features';
import useRenditionProfiles from './useRenditionProfiles';
import useExportPreset from './useExportPreset';
import { ExportPresetValue } from 'packages/hooks/useExportPreset';
import FilmStrip from './FilmStrip';
import { StyledInlineBanner } from './variationQuickExport.styled';
import { getCreative } from 'packages/store/creatives/creatives.selectors';
import { useFlyInNotificationContext } from '../../FlyInNotifications/FlyInNotificationsContext';
import { type ExportPayload } from 'packages/types/exports';
import exportService from 'packages/services/exportService';
import modalContainer from 'packages/common/modalContainer';

const QuickExportModal = ({ isOpen, onClose, onSwitchToAdvancedSelection }) => {
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const hasMultiVariationFeature = isPlatformAllowed([features.MULTI_VARIATION]);
    const hasMultiSizeFeature = isPlatformAllowed([features.MULTI_SIZE_CREATIVE]);
    const [isExporting, setIsExporting] = useState(false);
    const [isTranscribeEnabled, setIsTranscribeEnabled] = useState(false);
    const { creativeId, creativeName, creativeModel } = useDesign();
    const currentCreative = useSelector(getCreative);
    const { selectedIds, items, deselectAll, setSelectedIds } = useExport();
    const { presets, selectedPreset, setSelectedPreset, deselectPreset, bynderAccountId } = useExportPreset();
    const { renditionProfiles, selectedProfile, isLoadingRenditions, setSelectedProfile, deselectProfile } =
        useRenditionProfiles();
    const isVideoCreative = creativeModel instanceof MultiPageVideoModel;
    const isTranscribable = isVideoCreative && isPlatformAllowed([features.TRANSCRIPT]);

    const { showNotification } = useFlyInNotificationContext();
    const hasErrors = useMemo(
        () =>
            items.some(
                (item) =>
                    item.hasOversetText ||
                    item.entries.some((variation) => variation.hasDeletedAsset || variation.failureReason),
            ),
        [items],
    );

    const handleOnCloseInModal = () => {
        deselectAll();
        deselectPreset();
        deselectProfile();
        onClose();
    };

    const onPageVariationSelect = (variationId) => {
        if (variationId in selectedIds) {
            setSelectedIds(Object.fromEntries(Object.entries(selectedIds).filter(([key, _]) => key !== variationId)));
        } else {
            setSelectedIds({ ...selectedIds, [variationId]: true });
        }
    };

    const variationIds = useMemo(() => Object.keys(selectedIds), [selectedIds]);
    const total = items.reduce((acc, item) => acc + item.entries.length, 0);
    const validItems = useMemo(
        () =>
            items.filter(
                (item) =>
                    !item.hasOversetText &&
                    item.entries.some((variation) => !variation.hasDeletedAsset && !variation.failureReason),
            ),
        [items],
    );
    const totalValidItems = validItems.reduce((acc, item) => acc + item.entries.length, 0);
    const selectedCount = variationIds.length;
    const requiresRenditionConfiguration =
        selectedPreset.value === ExportPresetValue.BYNDER || selectedPreset.value === ExportPresetValue.DOWNLOAD;

    const selectAll = () => {
        setSelectedIds(
            Object.fromEntries(validItems.flatMap((item) => item.entries).map((entry) => [entry.variationId, true])),
        );
    };

    const getDownloadButtonTitle = () => {
        if (selectedPreset.value === ExportPresetValue.DOWNLOAD) {
            return translate('quick-export.modal.download', {
                count: selectedCount,
                kind: isVideoCreative ? 'video' : 'page',
            });
        }

        return translate('quick-export.modal.waiting-room');
    };

    const getDisableTooltip = () => {
        if (selectedPreset.value === ExportPresetValue.BYNDER) {
            return translate('modal.export.waiting-room-only.tooltip');
        }

        if (selectedPreset.value === ExportPresetValue.DOWNLOAD) {
            return translate('modal.export.download-only.tooltip');
        }

        return '';
    };

    const handleExportGeneration = () => {
        if (requiresRenditionConfiguration && !selectedProfile) {
            return;
        }

        let payload: ExportPayload = {
            name: selectedPreset.label,
            type: selectedPreset.value,
            variationIds,
        };

        if (selectedPreset.value === ExportPresetValue.BYNDER) {
            payload = {
                ...payload,
                renditionConfigurationId: selectedProfile.id,
                bynderAccountId,
            };
        } else if (selectedPreset.value === ExportPresetValue.DOWNLOAD) {
            payload = {
                ...payload,
                renditionConfigurationId: selectedProfile.id,
            };
        }

        setIsExporting(true);

        exportService
            .createExportJob(payload)
            .then(({ json, status }) => {
                if (status === 200) {
                    sendAppcuesEvent('Export job created', {
                        variationIds: payload.variationIds,
                        name: payload.name,
                        type: payload.type,
                    });

                    const notificationId = json.id;

                    showNotification({
                        notificationId,
                        show: true,
                        creativeName,
                        thumbnailUrl: currentCreative?.thumbnailImage,
                        exportType: selectedPreset.value,
                    });
                }
            })
            .catch(catchResponseError)
            .finally(() => {
                setIsExporting(false);
                handleOnCloseInModal();
            });
    };

    useEffect(() => {
        if (!hasMultiVariationFeature && isOpen) {
            selectAll();
        }
    }, [isOpen]);

    return (
        <ModalBase container={modalContainer} isOpen={isOpen} onClose={handleOnCloseInModal} size="small">
            <ModalBase.Header title={translate('quick-export.modal.title')} />
            <ModalBase.Content>
                <Form>
                    {(hasMultiVariationFeature || hasMultiSizeFeature) && (
                        <>
                            {hasErrors && (
                                <StyledInlineBanner variant="error" withMargin={!selectedCount}>
                                    {translate('modal.quick-export.selection-error-banner')}
                                </StyledInlineBanner>
                            )}
                            {!selectedCount && (
                                <InlineBanner variant="info">
                                    {translate('modal.quick-export.selection-banner')}
                                </InlineBanner>
                            )}
                            <Form.Row>
                                <Form.Group>
                                    <Flex alignItems="center">
                                        <Checkbox
                                            isChecked={selectedCount === totalValidItems}
                                            onChange={selectedCount === totalValidItems ? deselectAll : selectAll}
                                            isIndeterminate={selectedCount !== totalValidItems && selectedCount > 0}
                                        >
                                            {translate('modal.quick-export.select-all', {
                                                count: selectedCount,
                                                total,
                                            })}
                                        </Checkbox>
                                    </Flex>
                                </Form.Group>
                                {hasMultiVariationFeature && (
                                    <Form.Group>
                                        <Flex justifyContent="flex-end">
                                            <Button variant="clean" onClick={onSwitchToAdvancedSelection}>
                                                {translate('modal.quick-export.change-selection')}
                                            </Button>
                                        </Flex>
                                    </Form.Group>
                                )}
                            </Form.Row>
                            <Form.Group>
                                <FilmStrip
                                    items={items}
                                    validVariations={validItems}
                                    selectedIds={selectedIds}
                                    onItemSelect={onPageVariationSelect}
                                />
                            </Form.Group>
                        </>
                    )}
                    <Form.Group>
                        <Form.Label>{translate('modal.quick-export.preset')}</Form.Label>
                        <Dropdown
                            trigger={({ isOpen, ...triggerProps }) => (
                                <Button
                                    isPressed={isOpen}
                                    isDisabled={isExporting}
                                    icon={selectedPreset.icon}
                                    rightIcon={<Dropdown.Arrow />}
                                    {...triggerProps}
                                    isFullWidth
                                >
                                    <Dropdown.SelectButtonText>
                                        {isLoadingRenditions
                                            ? translate('modal.quick-export.loading')
                                            : selectedPreset?.label}
                                    </Dropdown.SelectButtonText>
                                </Button>
                            )}
                            position="bottom"
                        >
                            {presets.map((preset) =>
                                preset.disabled ? (
                                    <Tooltip content={getDisableTooltip()} key={preset.value} position="bottom-left">
                                        <Dropdown.Item
                                            key={preset.value}
                                            onClick={() => setSelectedPreset(preset)}
                                            isChecked={preset.value === selectedPreset.value}
                                            isDisabled={preset.disabled}
                                            icon={preset.icon}
                                        >
                                            {preset.label}
                                        </Dropdown.Item>
                                    </Tooltip>
                                ) : (
                                    <Dropdown.Item
                                        key={preset.value}
                                        onClick={() => setSelectedPreset(preset)}
                                        isChecked={preset.value === selectedPreset.value}
                                        isDisabled={preset.disabled}
                                        icon={preset.icon}
                                    >
                                        {preset.label}
                                    </Dropdown.Item>
                                ),
                            )}
                        </Dropdown>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>{translate('modal.quick-export.format')}</Form.Label>
                        <Dropdown
                            trigger={({ isOpen, ...triggerProps }) => (
                                <Button
                                    isPressed={isOpen}
                                    isDisabled={isLoadingRenditions || !renditionProfiles.length}
                                    rightIcon={<Dropdown.Arrow />}
                                    {...triggerProps}
                                    isFullWidth
                                >
                                    <Dropdown.SelectButtonText>
                                        {isLoadingRenditions
                                            ? translate('modal.quick-export.loading')
                                            : selectedProfile?.displayName}
                                    </Dropdown.SelectButtonText>
                                </Button>
                            )}
                            position="bottom"
                        >
                            {renditionProfiles.map((profile) => (
                                <Dropdown.ListItem
                                    key={profile.id}
                                    onClick={() => setSelectedProfile(profile)}
                                    isChecked={profile.id === selectedProfile?.id}
                                    subtext={profile.description}
                                >
                                    {profile.displayName}
                                </Dropdown.ListItem>
                            ))}
                        </Dropdown>
                    </Form.Group>
                    {isTranscribable && (
                        <CheckboxGroup label={translate('modal.quick-export.transcribe.title')}>
                            <Checkbox
                                onChange={() => setIsTranscribeEnabled((prev) => !prev)}
                                isChecked={isTranscribeEnabled}
                            >
                                {translate('modal.quick-export.transcribe.label')}
                            </Checkbox>
                        </CheckboxGroup>
                    )}
                </Form>
            </ModalBase.Content>
            <ModalBase.Footer
                actionPrimary={
                    <Button
                        variant="primary"
                        onClick={handleExportGeneration}
                        isLoading={isExporting}
                        isDisabled={!variationIds.length || (requiresRenditionConfiguration && !selectedProfile)}
                        title={getDownloadButtonTitle()}
                    >
                        {getDownloadButtonTitle()}
                    </Button>
                }
                actionSecondary={
                    <Button
                        variant="secondary"
                        onClick={handleOnCloseInModal}
                        title={translate('quick-export.modal.cancel')}
                    >
                        {translate('quick-export.modal.cancel')}
                    </Button>
                }
            />
        </ModalBase>
    );
};

export default QuickExportModal;
